import {
    assistantAsking, assistantAskingForAction,
    assistantAskingForContext, assistantAskingForExample,
    assistantAskingForGenre, assistantAskingForInstructions, assistantAskingForRequirements, assistantAskingForSchema,
    assistantAskingForSynopsis, systemInstruction, userSaying
} from "../../../../../../../ai/Utils";
import {DraftChapter, DraftScene, DraftSection} from "../../../../../../../feature/Draft";


export const generateSectionsPrompt = (vars: {
    chapter: DraftChapter,
    prevChapter?: DraftChapter,
    nextChapter?: DraftChapter,
    genres: string[],
    synopsis: string,
    prevSections?: { section: number, content: string }[] | undefined,
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши кратко главу, над которой ведется работа", vars.chapter.description))
    .concat(assistantAsking("Опиши кратко предыдущую главу", vars.prevChapter?.description))
    .concat(assistantAsking("Опиши кратко следующую главу", vars.nextChapter?.description))
    .concat(assistantAsking("Опиши содержимое предыдущих секций", vars.prevSections ? JSON.stringify(vars.prevSections) : undefined))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        "Проанализируй содержимое предыдущих секций",
        "Придумай продолжение согласно содержимому главы",
        "Наполни секции деталями и диалогами",
    ]))
    .concat(assistantAskingForRequirements([
        "Действия и события в секциях не должны повторяться",
        "Секции должны быть связаны с содержимым секции главы",
        "Не придумывай ничего, что не описано в кратком содержании главы",
        "Не забегай вперед по сюжету, описывай только то событие, которое происходит в текущей главе",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": [{"id":number, "title":string, "description":string}],}'))
    .concat(assistantAskingForExample({
            data: [
                {
                    id: 1,
                    title: "Находка",
                    description: "- В ночь на 31 декабря главный герой отправляется в поход и находит магический артефакт.\n\n- Он приносит артефакт домой и замечает изменения вокруг себя"
                }
            ]
        }
    ))
    .concat(assistantAskingForAction(`Сгенерируй сцены для секции главы, над которой ведется работа.`))

export const generateSectionPrompt = (vars: {
    genres: string[],
    synopsis: string,
    section: number,
    sections: DraftSection[],
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши все секции главы", JSON.stringify(vars.sections.map((item) => ({
        section: item.id,
        content: item.description
    })))))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        "Придумай содержимое секции",
        "Проработай содержимое секции"
    ]))
    .concat(assistantAskingForRequirements([
        "Объем текста должен оставаться таким же как в исходной секции",
        "События не должны повторяться с другими секциями",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": {"id":number, "title":string, "description":string}'))
    .concat(assistantAskingForExample(
        {
            data: {
                id: 1,
                title: "Находка",
                description: "- В ночь на 31 декабря главный герой отправляется в поход и находит магический артефакт.\n\nОн приносит артефакт домой и замечает изменения вокруг себя."
            }
        }
    ))
    .concat(assistantAskingForAction(`Перегенерируй существующую или сгенерируй новую ${vars.section}-ю секцию главы.`))


export const improveSectionPrompt = (vars: {
    section: DraftSection,
    genres: string[],
    synopsis: string,
    sections: DraftSection[],
    context?: string[]
}) => systemInstruction("Ты профессиональный писатель")
    .concat(userSaying("Я работаю над созданием литературного произведения"))
    .concat(assistantAskingForGenre(vars.genres))
    .concat(assistantAskingForSynopsis(vars.synopsis))
    .concat(assistantAsking("Опиши все секции главы", JSON.stringify(vars.sections.map((item) => ({
        section: item.id,
        content: item.description
    })))))
    .concat(assistantAskingForContext(vars.context))
    .concat(assistantAskingForInstructions([
        `Проанализируй краткое содержание ${vars.section.id}-й секции`,
        "Наполни секцию деталями, чтобы разнообразить сюжет",
        "Добавь действия и события, которые могут произойти в секции",
        "Можно добавить вне сюжетные события, истории, рассказы, если это необходимо для раскрытия смысла или объяснения неочевидных моментов",
        "Убедись, что добавленные действия и события соответствуют содержимому секции",
        "Убедись, что добавленные действия и события не забегают вперед по сюжету",
        "Разбей содержимое секции на абзацы"
    ]))
    .concat(assistantAskingForRequirements([
        "Действия и события в секциях не должны повторяться",
        "Результат должен быть в формате json"
    ]))
    .concat(assistantAskingForSchema('{"data": {"id":number, "title":string, "description":string}'))
    .concat(assistantAskingForExample({
            data: {
                id: 1,
                title: "Осмотр места преступления",
                description: "Детектив наслаждается пейзажем рядом с местом преступления. После он отправляется на место преступления и разговаривает с криминалистами. Криминалисты рассказывают ему о найденных уликах. У детектива начинает складываться картина произошедшего. Он мысленно составляет психологический портрет преступника. Детектив возвращается в участок"
            }
        }
    ))
    .concat(assistantAskingForAction(`Наполни ${vars.section.id}-ю секцию.`))